import queryString from 'query-string';

import { Topic } from './content';
import { UserModel } from './user';

const COURSE_API_ROOT = '/api/course';
const CLASSROOM_API_ROOT = '/api/classroom';
const CURRICULUM_API_ROOT = '/api/curriculum';

interface Curriculum<
  HydrateTopics extends boolean = false,
  HydrateAdministrators extends boolean = false,
> {
  _id: string;
  topics: Array<HydrateTopics extends true ? Topic<false, false> : string>;
  administrators: Array<
    HydrateAdministrators extends true ? UserModel : string
  >;
}

interface Classroom<
  HydrateCurriculum extends boolean = false,
  HydrateTopics extends boolean = false,
  HydrateAdministrators extends boolean = false,
> {
  _id: string;
  slug: string;
  name: string;
  termStart: string;
  termEnd: string;
  institution: string;
  level: string;
  teachers: string[];
  assistants: string[];
  students: string[];
  curriculum: HydrateCurriculum extends true
    ? Curriculum<HydrateTopics, HydrateAdministrators>
    : string;
  started: boolean;
  ended: boolean;
  active: boolean;
}

export function getMyUnendedClassrooms<WithCurriculum extends boolean = false>(
  withCurriculum: WithCurriculum,
): Promise<Array<Classroom<WithCurriculum>>>;
export async function getMyUnendedClassrooms(withCurriculum: boolean = false) {
  const params: Record<string, any> = {};
  if (withCurriculum) params.populate = ['curriculum'];
  const resp = await fetch(
    `${CLASSROOM_API_ROOT}/mine/unended?${queryString.stringify(params)}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  if (resp.ok) return await resp.json();
  throw new Error(await resp.text());
}

export async function addTopicsToCurriculum(
  curriculumId: string,
  topicIds: string[],
): Promise<Curriculum<true, true>> {
  const resp = await fetch(`${CURRICULUM_API_ROOT}/${curriculumId}/topics`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ topicIds }),
  });

  if (resp.ok) return await resp.json();
  throw new Error(await resp.text());
}

export async function removeTopicsFromCurriculum(
  curriculumId: string,
  topicIds: string[],
): Promise<Curriculum<true, true>> {
  const resp = await fetch(
    `${CURRICULUM_API_ROOT}/${curriculumId}/topics?${queryString.stringify({
      topicIds,
    })}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  if (resp.ok) return await resp.json();
  throw new Error(await resp.text());
}

export async function getClassroomProgressForCourse(
  classroomId: string,
  courseId: string,
): Promise<{
  classroom: {
    name: string;
    manageUrl: string;
  };
  total: number;
  completed: number;
}> {
  const resp = await fetch(
    `${COURSE_API_ROOT}/${courseId}/progress/${classroomId}`,
  );

  if (resp.ok) return await resp.json();
  throw new Error(await resp.text());
}
